import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import FormRenderer from '../shared/FormRenderer';
import './DistrictsList.css';

const DistrictFormInstancesList = () => {
  const { subdomain } = useParams();
  const navigate = useNavigate();

  const [district, setDistrict] = useState(null);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trashConfirm, setTrashConfirm] = useState(null);
  const [viewModal, setViewModal] = useState({ show: false, instance: null, formData: null, loading: false });

  useEffect(() => {
    fetchDistrictInstances();
  }, [subdomain]);

  const fetchDistrictInstances = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/form-instances/district-by-subdomain/${subdomain}`);
      setDistrict(response.data.district);
      setInstances(response.data.form_instances);
    } catch (error) {
      console.error('Failed to fetch form instances:', error);
      if (error.response?.status === 404) {
        alert('District not found');
        navigate('/superadmin/form-instances');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTrash = async (id) => {
    try {
      await axios.delete(`/api/form-instances/${id}`);
      setInstances(instances.filter(i => i.id !== id));
      setTrashConfirm(null);
      alert('Form instance moved to trash');
    } catch (error) {
      console.error('Failed to trash instance:', error);
      alert('Failed to move form instance to trash');
    }
  };

  const handleViewForm = async (instance) => {
    setViewModal({ show: true, instance, formData: null, loading: true });

    try {
      const response = await axios.get(`/api/form-instances/${instance.id}`);
      setViewModal({
        show: true,
        instance,
        formData: response.data.form_data,
        loading: false
      });
    } catch (error) {
      console.error('Failed to fetch form data:', error);
      alert('Failed to load form data');
      setViewModal({ show: false, instance: null, formData: null, loading: false });
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!district) {
    return (
      <div className="districts-list">
        <div className="error-message">District not found</div>
        <Link to="/superadmin/form-instances" className="btn-secondary">Back to Districts</Link>
      </div>
    );
  }

  return (
    <div className="districts-list">
      <div className="page-header">
        <div>
          <button 
            onClick={() => navigate('/superadmin/form-instances')} 
            className="btn-secondary"
            style={{ marginRight: '1rem' }}
          >
            ← Back to Districts
          </button>
          <h2 style={{ display: 'inline-block', margin: 0 }}>Form Instances: {district.district_name}</h2>
        </div>
        <Link to={`/superadmin/form-instances/${subdomain}/create`} className="btn-primary">Create Form Instance</Link>
      </div>

      {trashConfirm && (
        <div className="modal-overlay" onClick={() => setTrashConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Move Form Instance to Trash</h3>
            <p>Are you sure you want to move this form instance to the trash? It can be restored later from the Trashed Forms section.</p>
            <div className="modal-actions">
              <button onClick={() => handleTrash(trashConfirm)} className="btn-delete">Move to Trash</button>
              <button onClick={() => setTrashConfirm(null)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {viewModal.show && (
        <div className="modal-overlay" onClick={() => setViewModal({ show: false, instance: null, formData: null, loading: false })}>
          <div className="modal large-modal" onClick={(e) => e.stopPropagation()}>
            <h3>View Form: {viewModal.instance?.instance_name}</h3>
            {viewModal.loading ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>Loading form...</div>
            ) : viewModal.formData ? (
              <div className="form-preview-container">
                <FormRenderer
                  formData={viewModal.formData}
                  formValues={{}}
                  onChange={() => {}} // No-op since it's read-only
                  readOnly={true}
                />
              </div>
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#e74c3c' }}>
                Failed to load form data
              </div>
            )}
            <div className="modal-actions" style={{ marginTop: '2rem' }}>
              <button
                onClick={() => setViewModal({ show: false, instance: null, formData: null, loading: false })}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>Instance Name</th>
            <th>Master Form</th>
            <th>School Year</th>
            <th>Version</th>
            <th>Display Order</th>
            <th>Required</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {instances.length === 0 ? (
            <tr>
              <td colSpan="8" style={{ textAlign: 'center' }}>No active form instances found for this district</td>
            </tr>
          ) : (
            instances.map(instance => (
              <tr key={instance.id}>
                <td><strong>{instance.instance_name}</strong></td>
                <td>{instance.master_form_name}</td>
                <td>{instance.school_year || '-'}</td>
                <td>{instance.version_number}</td>
                <td>{instance.display_order}</td>
                <td>{instance.is_required ? <span style={{ color: '#e74c3c' }}>Yes</span> : 'No'}</td>
                <td>{new Date(instance.created_at).toLocaleDateString()}</td>
                <td className="actions">
                  <button onClick={() => handleViewForm(instance)} className="btn-edit">View</button>
                  <button onClick={() => setTrashConfirm(instance.id)} className="btn-delete">Trash</button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DistrictFormInstancesList;

